import { Space } from 'antd';
import { Message, MessageTypeEnum, useGetFileMessageQuery } from 'api/MessagesApi';
import MessageIcon from 'components/Message/MessageIcon';
import { AppAudioPlayer } from 'components/UiKit/Media/AppAudioPlayer';
import { AppVideoPlayer } from 'components/UiKit/Media/AppVideoPlayer';
import { TextToHtmlComponent } from 'components/UiKit/TextToHtmlComponent/TextToHtmlComponent';
import { useEffect, useMemo } from 'react';
import { isAudioFile, isVideoFile } from 'utils/utils';

type AllProps = {
  message: Message;
  file?: File | null;
  text?: string;
};

function ContentPreviewItem({ message, file, text }: AllProps) {
  const { data: messageFile } = useGetFileMessageQuery({ id: message.id }, { skip: !message.fileName });

  const audioObjectUrl = useMemo(() => {
    if (file?.name && isAudioFile(file.name)) {
      return URL.createObjectURL(file);
    }
    return null;
  }, [file]);

  const videoObjectUrl = useMemo(() => {
    if (file?.name && isVideoFile(file.name)) {
      return URL.createObjectURL(file);
    }
    return null;
  }, [file]);

  useEffect(() => {
    return () => {
      if (audioObjectUrl) {
        URL.revokeObjectURL(audioObjectUrl);
      }
      if (videoObjectUrl) {
        URL.revokeObjectURL(videoObjectUrl);
      }
    };
  }, [audioObjectUrl, videoObjectUrl]);

  return (
    <div className="media-overlay">
      {message.type === MessageTypeEnum.Audio &&
        (() => {
          const fallback = (
            <div className="selected-file">
              <Space size="small">
                <MessageIcon type={message.type} />
                {message.fileName}
              </Space>
            </div>
          );

          if (audioObjectUrl) {
            return (
              <AppAudioPlayer url={audioObjectUrl} mimeType={file?.type} fileName={file?.name} fallback={fallback} />
            );
          }

          if (messageFile?.signedUrl && isAudioFile(message.fileName)) {
            return <AppAudioPlayer url={messageFile.signedUrl} fileName={message.fileName} fallback={fallback} />;
          }

          return fallback;
        })()}

      {message.type === MessageTypeEnum.Video &&
        (() => {
          const fallback = (
            <div className="selected-file">
              <Space size="small">
                <MessageIcon type={message.type} />
                {message.fileName}
              </Space>
            </div>
          );

          if (videoObjectUrl) {
            return (
              <AppVideoPlayer url={videoObjectUrl} mimeType={file?.type} fileName={file?.name} fallback={fallback} />
            );
          }

          if (messageFile?.signedUrl && isVideoFile(message.fileName)) {
            return <AppVideoPlayer url={messageFile.signedUrl} fileName={message.fileName} fallback={fallback} />;
          }

          return fallback;
        })()}

      {message.type === MessageTypeEnum.Text && (
        <>
          {file ? (
            <div className="selected-file">
              <Space size="small">
                <MessageIcon type={message.type} />
                {file.name}
              </Space>
            </div>
          ) : message.fileName ? (
            <div className="selected-file">
              <Space size="small">
                <MessageIcon type={message.type} />
                {message.fileName}
              </Space>
            </div>
          ) : (
            <div className="draft-text">
              <TextToHtmlComponent text={text || message.content} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ContentPreviewItem;
